import { z } from 'zod';

import { EXTERNAL_CALL_PAGE_MAX } from '@units/external-call-log/model/constants/external-call-page.constant.js';
import { EXTERNAL_CALL_OUTCOMES } from '@units/external-call-log/model/enums/external-call.enums.js';

/**
 * The state of the journal, as the URL carries it (`rules/lists-and-filters.mdc` §1).
 *
 * The whole filter lives here rather than in component state, and for this screen that is a
 * requirement rather than a convention: STORY-005-06 asks that an auditor's period, provider and
 * outcome «переживают перезагрузку и пересылку ссылки коллеге» — a finding in an attestation review
 * is a link, not a description of six clicks.
 *
 * **Days, not instants.** The parameter an auditor sets is a calendar day; the instants the contract
 * takes are computed from it at the boundary (`lib/utils/day-bounds.util.ts`), so the address stays
 * readable and the bound stays inclusive.
 *
 * **Every field falls back with `.catch` rather than `.default`.** A default fills in an *absent*
 * value; a present one that means nothing — a provider id that was deleted, `outcome=REFUSED`, a
 * page that is a word — fails validation, and a failed `validateSearch` replaces the screen with the
 * error boundary. With `replace: true` on every filter change that broken address also stays in the
 * bar, so a reload does not help.
 */

/** How many rows a page carries unless the address says otherwise. */
export const EXTERNAL_CALL_DEFAULT_PER_PAGE = 50;

/**
 * The sizes the screen offers, ending at the contract's own maximum.
 *
 * The wide page exists for one caller — somebody pulling a period out for an attestation review —
 * and 200 is where the operation stops accepting it, so offering more would be offering a `422`.
 */
export const EXTERNAL_CALL_PER_PAGE_CHOICES = [
  EXTERNAL_CALL_DEFAULT_PER_PAGE,
  100,
  EXTERNAL_CALL_PAGE_MAX,
] as const;

export const externalCallSearchSchema = z.object({
  /** Inclusive, as a calendar day in the reader's zone. */
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  /** A foreign or unknown id narrows the journal to nothing; it is a filter, not an address. */
  provider: z.uuid().optional().catch(undefined),
  outcome: z.enum(EXTERNAL_CALL_OUTCOMES).optional().catch(undefined),
  page: z.coerce.number().int().min(1).catch(1),
  perPage: z.coerce
    .number()
    .int()
    .min(1)
    .max(EXTERNAL_CALL_PAGE_MAX)
    .catch(EXTERNAL_CALL_DEFAULT_PER_PAGE),
});

export type ExternalCallSearch = z.infer<typeof externalCallSearchSchema>;
